/**
 * Single source of truth for `data-testid` values (W2, FR-016).
 *
 * The Playwright suite is the parity contract; it selects exclusively by these
 * ids. Always render `data-testid={TESTID.x}` — never a string literal — so the
 * compiler catches typos and refactors stay grep-able.
 *
 * Inventory mirrors specs/117-web-ui-react/data-model.md §4. A snapshot test
 * (testids.test.ts) guards against drift.
 */
import type { JobSpec } from './schemas'

export const TESTID = {
  // ── Global / layout ────────────────────────────────────────────────
  sidebar: 'sidebar',
  syncingBar: 'syncing-bar',
  userMenuTrigger: 'user-menu-trigger',
  userMenuDropdown: 'user-menu-dropdown',
  menuSettings: 'menu-settings',
  logoutButton: 'logout-button',
  themeToggle: 'theme-toggle',
  languageSwitcher: 'language-switcher',
  languageOption: (code: string) => `language-option-${code}` as const,
  // Mobile top-header user menu (distinct ids — the desktop sidebar menu is
  // always in the DOM, just hidden, so shared ids would double-match).
  userMenuTriggerMobile: 'user-menu-trigger-mobile',
  userMenuDropdownMobile: 'user-menu-dropdown-mobile',
  menuSettingsMobile: 'menu-settings-mobile',
  logoutButtonMobile: 'logout-button-mobile',
  betaPending: 'beta-pending',
  navJobs: 'nav-jobs',
  navAdminMachines: 'nav-admin-machines',
  navAdminUsers: 'nav-admin-users',
  navAdminOrganizations: 'nav-admin-organizations',
  navAdminOperations: 'nav-admin-operations',
  navAdminCodeAnalysis: 'nav-admin-code-analysis',
  navProductAnalytics: 'nav-product-analytics',
  statusBadge: 'status-badge',
  noticeSuccess: 'notice-success',
  noticeError: 'notice-error',

  // ── System administration ──────────────────────────────────────────
  adminMachinesHeading: 'admin-machines-heading',
  adminUsersHeading: 'admin-users-heading',
  adminOperationsHeading: 'admin-operations-heading',
  adminCodeAnalysisHeading: 'admin-code-analysis-heading',
  productAnalyticsHeading: 'product-analytics-heading',
  productAnalyticsPage: 'product-analytics-page',
  productAnalyticsTimeline: 'product-analytics-timeline',
  adminUsersTable: 'admin-users-table',
  userRoleFilter: 'user-role-filter',
  userBetaFilter: 'user-beta-filter',
  userVerifiedFilter: 'user-verified-filter',
  acceptBetaButton: (id: string) => `accept-beta-${id}` as const,
  organizationCreationButton: (id: string) =>
    `organization-creation-${id}` as const,

  // ── Job list ───────────────────────────────────────────────────────
  jobListHeading: 'job-list-heading',
  jobListError: 'job-list-error',
  jobListRetry: 'job-list-retry',
  statusFilterSelect: 'status-filter-select',
  modeFilterSelect: 'mode-filter-select',
  typeFilterSelect: 'type-filter-select',
  jobsTable: 'jobs-table',
  jobsTableColumns: 'jobs-table-columns',
  jobRow: (id: string) => `job-row-${id}` as const,
  filter: (
    status:
      | 'all'
      | 'preparing'
      | 'queued'
      | 'provisioning'
      | 'rendering'
      | 'uploading'
      | 'done'
      | 'delivering'
      | 'delivered'
      | 'delivery_failed'
      | 'failed'
      | 'cancelled',
  ) => `filter-${status}` as const,
  modeFilter: (mode: 'all' | 'animation' | 'tour' | 'turntable' | 'topview') =>
    `mode-filter-${mode}` as const,
  typeFilter: (kind: JobSpec['type']) => `type-filter-${kind}` as const,
  emptyState: 'empty-state',
  filterNoResults: 'filter-no-results',
  emptyStateMessage: 'empty-state-message',
  emptyStateCta: 'empty-state-cta',
  blenderPluginLink: 'blender-plugin-link',

  // ── Job detail ─────────────────────────────────────────────────────
  jobNotFound: 'job-not-found',
  jobLoadError: 'job-load-error',
  jobAttempts: 'job-attempts',
  jobAttempt: (id: string) => `job-attempt-${id}` as const,
  jobRecovery: 'job-recovery',
  jobParameters: 'job-parameters',
  jobDetailName: 'job-detail-name',
  jobDetailTabOverview: 'job-detail-tab-overview',
  jobDetailTabLogs: 'job-detail-tab-logs',
  pipelineProgress: 'pipeline-progress',
  progressLabel: 'progress-label',
  frameGallery: 'frame-gallery',
  frameLightbox: 'frame-lightbox',
  frameThumb: (index: number) => `frame-thumb-${index}` as const,
  downloadButton: 'download-button',
  downloadAllButton: 'download-all-button',
  viewTourButton: 'view-tour-button',
  jobLogsPanel: 'job-logs-panel',
  jobLogsScroll: 'job-logs-scroll',
  cancelButton: 'cancel-button',
  rerunButton: 'rerun-button',
  cancelDialog: 'cancel-dialog',
  cancelConfirm: 'cancel-confirm',
  cancelDismiss: 'cancel-dismiss',

  // ── New job ────────────────────────────────────────────────────────
  newJobHeading: 'new-job-heading',
  jobKindTile: (
    kind: 'blender_render' | 'empty' | 'upscaling' | 'turntable_frame_bake',
  ) => `job-kind-tile-${kind}` as const,
  jobKindBack: 'job-kind-back',
  engineSelect: 'engine-select',
  formatSelect: 'format-select',
  waitSecsInput: 'wait-secs-input',
  waitSecsError: 'wait-secs-error',
  exitCodeInput: 'exit-code-input',
  exitCodeError: 'exit-code-error',
  turntableColorViewId: 'turntable-color-view-id',
  turntableColorViewIdError: 'turntable-color-view-id-error',
  turntableDepthViewId: 'turntable-depth-view-id',
  turntableDepthViewIdError: 'turntable-depth-view-id-error',
  turntableOutputTargetId: 'turntable-output-target-id',
  turntableOutputTargetIdError: 'turntable-output-target-id-error',
  turntableDeliveryApiKey: 'turntable-delivery-api-key',
  turntableDeliveryApiKeyError: 'turntable-delivery-api-key-error',
  turntableOutputFrameCount: 'turntable-output-frame-count',
  turntableOutputFrameCountError: 'turntable-output-frame-count-error',
  turntableManifestFile: 'turntable-manifest-file',
  turntableManifestFileError: 'turntable-manifest-file-error',
  sceneFileButton: 'scene-file-button',
  sceneFileInput: 'scene-file-input',
  sceneFileError: 'scene-file-error',
  selectedFileName: 'selected-file-name',
  selectedFileSize: 'selected-file-size',
  removeFileButton: 'remove-file-button',
  submitButton: 'submit-button',
  submitError: 'submit-error',
  jobPreviewSheet: 'job-preview-sheet',
  newJobSheet: 'new-job-sheet',
  newJobButton: 'new-job-button',

  // ── Login ──────────────────────────────────────────────────────────
  loginHeading: 'login-heading',
  loginSubmit: 'login-submit',
  registerSubmit: 'register-submit',
  oauthGoogle: 'oauth-google',
  oauthGithub: 'oauth-github',

  // ── Verify email ───────────────────────────────────────────────────
  verifyCodeInput: 'verify-code-input',
  verifySubmit: 'verify-submit',
  resendCode: 'resend-code',
  goToLogin: 'go-to-login',

  // ── Settings ───────────────────────────────────────────────────────
  settingsHeading: 'settings-heading',
  settingsProfile: 'settings-profile',
  settingsOrganizations: 'settings-organizations',

  // ── API keys ───────────────────────────────────────────────────────
  apiKeysHeading: 'api-keys-heading',
  apiKeysGenerateButton: 'api-keys-generate-button',
  apiKeysList: 'api-keys-list',
  apiKeysEmptyState: 'api-keys-empty-state',
  apiKeysEmptyMessage: 'api-keys-empty-message',
  apiKeyRow: 'api-key-row',
  apiKeyName: 'api-key-name',
  apiKeyRevokeButton: 'api-key-revoke-button',
  apiKeyRevokedBadge: 'api-key-revoked-badge',
  generateApiKeyModal: 'generate-api-key-modal',
  generateApiKeyInput: 'generate-api-key-input',
  generateApiKeySubmit: 'generate-api-key-submit',
  generateApiKeyCancel: 'generate-api-key-cancel',
  createdApiKeyModal: 'created-api-key-modal',
  createdApiKeyValue: 'created-api-key-value',
  createdApiKeyCopy: 'created-api-key-copy',
  createdApiKeyDone: 'created-api-key-done',
  revokeApiKeyModal: 'revoke-api-key-modal',
  revokeApiKeyConfirm: 'revoke-api-key-confirm',
  revokeApiKeyCancel: 'revoke-api-key-cancel',
  // External-app API keys (admin area).
  extKeysGenerateButton: 'ext-keys-generate-button',
  extKeysList: 'ext-keys-list',
  extKeysEmptyState: 'ext-keys-empty-state',
  extKeyRow: 'ext-key-row',
  extKeyName: 'ext-key-name',
  extKeyRevokeButton: 'ext-key-revoke-button',
  extKeyRevokedBadge: 'ext-key-revoked-badge',
  generateExtKeyModal: 'generate-ext-key-modal',
  generateExtKeyNameInput: 'generate-ext-key-name-input',
  generateExtKeyScope: 'generate-ext-key-scope',
  generateExtKeyExpiresInput: 'generate-ext-key-expires-input',
  generateExtKeySubmit: 'generate-ext-key-submit',
  generateExtKeyCancel: 'generate-ext-key-cancel',
  createdExtKeyModal: 'created-ext-key-modal',
  createdExtKeyValue: 'created-ext-key-value',
  createdExtKeyCopy: 'created-ext-key-copy',
  createdExtKeyDone: 'created-ext-key-done',
  revokeExtKeyModal: 'revoke-ext-key-modal',
  revokeExtKeyConfirm: 'revoke-ext-key-confirm',
  revokeExtKeyCancel: 'revoke-ext-key-cancel',

  // ── Organizations ──────────────────────────────────────────────────
  navOrganizations: 'nav-organizations',
  orgSwitcher: 'org-switcher',
  orgSwitcherCurrent: 'org-switcher-current',
  orgSwitcherTrigger: 'org-switcher-trigger',
  orgSwitcherPersonal: 'org-switcher-personal',
  orgSwitcherOption: (id: string) => `org-switcher-option-${id}` as const,
  orgSwitcherManage: 'org-switcher-manage',
  orgSwitcherCreate: 'org-switcher-create',
  // Prominent pending-invitations banner (app shell)
  invitationsBanner: 'invitations-banner',
  invitationBannerRow: 'invitation-banner-row',
  invitationBannerAccept: (id: string) =>
    `invitation-banner-accept-${id}` as const,
  invitationBannerReject: (id: string) =>
    `invitation-banner-reject-${id}` as const,
  orgsList: 'orgs-list',
  orgsEmptyState: 'orgs-empty-state',
  orgRow: 'org-row',
  orgName: 'org-name',
  createOrgButton: 'create-org-button',
  createOrgModal: 'create-org-modal',
  createOrgNameInput: 'create-org-name-input',
  createOrgSubmit: 'create-org-submit',
  createOrgCancel: 'create-org-cancel',
  membersList: 'members-list',
  memberRow: 'member-row',
  memberEmail: 'member-email',
  memberRoleSelect: 'member-role-select',
  memberRemoveButton: 'member-remove-button',
  inviteMemberButton: 'invite-member-button',
  inviteMemberModal: 'invite-member-modal',
  inviteMemberEmailInput: 'invite-member-email-input',
  inviteMemberRoleSelect: 'invite-member-role-select',
  inviteMemberSubmit: 'invite-member-submit',
  inviteMemberCancel: 'invite-member-cancel',
  invitationsList: 'invitations-list',
  invitationRow: 'invitation-row',
  orgMembersTab: 'org-members-tab',
  pluginAccessTab: 'plugin-access-tab',
  pluginAccessHeading: 'plugin-access-heading',
  pluginAccessLoading: 'plugin-access-loading',
  pluginAccessLoadError: 'plugin-access-load-error',
  pluginAccessProductUnavailable: 'plugin-access-product-unavailable',
  pluginAccessEmpty: 'plugin-access-empty',
  pluginAccessMembers: 'plugin-access-members',
  pluginAccessMemberRow: 'plugin-access-member-row',
  pluginAccessState: (membershipId: string) =>
    `plugin-access-state-${membershipId}` as const,
  pluginAccessChange: (membershipId: string) =>
    `plugin-access-change-${membershipId}` as const,
  pluginAccessConfirmDialog: 'plugin-access-confirm-dialog',
  pluginAccessConfirmSubmit: 'plugin-access-confirm-submit',
  pluginAccessConfirmCancel: 'plugin-access-confirm-cancel',
  pluginAccessSuccess: 'plugin-access-success',
  pluginAccessConflict: 'plugin-access-conflict',
  pluginAccessMutationError: 'plugin-access-mutation-error',
  pluginAccessHistory: 'plugin-access-history',
  // System Administration → Organizations (product entitlements)
  adminOrgsSearch: 'admin-orgs-search',
  adminOrgsOriginFilter: 'admin-orgs-origin-filter',
  adminOrgsTable: 'admin-orgs-table',
  adminOrgsEmpty: 'admin-orgs-empty',
  adminOrgsLoadError: 'admin-orgs-load-error',
  adminOrgsMutationError: 'admin-orgs-mutation-error',
  adminOrgRow: (id: string) => `admin-org-row-${id}` as const,
  adminOrgPloteaState: (id: string) => `admin-org-plotea-state-${id}` as const,
  adminOrgPloteaSwitch: (id: string) =>
    `admin-org-plotea-switch-${id}` as const,
  adminOrgExpiryButton: (id: string) =>
    `admin-org-expiry-button-${id}` as const,
  adminOrgHistoryButton: (id: string) =>
    `admin-org-history-button-${id}` as const,
  adminOrgProductDialog: 'admin-org-product-dialog',
  adminOrgExpiryInput: 'admin-org-expiry-input',
  adminOrgNoExpiry: 'admin-org-no-expiry',
  adminOrgTrial: 'admin-org-trial',
  adminOrgPreset14: 'admin-org-preset-14',
  adminOrgPreset30: 'admin-org-preset-30',
  adminOrgReasonInput: 'admin-org-reason-input',
  adminOrgDialogError: 'admin-org-dialog-error',
  adminOrgDialogSubmit: 'admin-org-dialog-submit',
  adminOrgDialogCancel: 'admin-org-dialog-cancel',
  adminOrgHistory: 'admin-org-history',
  // Onboarding (org-only accounts with no active org)
  onboardingScreen: 'onboarding-screen',
  onboardingOrgs: 'onboarding-orgs',
  onboardingEnterOrg: (id: string) => `onboarding-enter-org-${id}` as const,
  onboardingInvites: 'onboarding-invites',
  onboardingInviteRow: 'onboarding-invite-row',
  onboardingAcceptInvite: (id: string) =>
    `onboarding-accept-invite-${id}` as const,
  onboardingRejectInvite: (id: string) =>
    `onboarding-reject-invite-${id}` as const,
  onboardingLogout: 'onboarding-logout',
  onboardingCreateOrg: 'onboarding-create-org',
  onboardingVerifyEmail: 'onboarding-verify-email',
  noPlanScreen: 'no-plan-screen',
  noPlanOrgName: 'no-plan-org-name',
  noPlanContact: 'no-plan-contact',
  noPlanEnterOrg: (id: string) => `no-plan-enter-org-${id}` as const,
  noPlanCreateOrg: 'no-plan-create-org',
  noPlanLogout: 'no-plan-logout',
} as const
